import { Typeahead, type Key } from "@zapier/design-system-beta";
import { Text } from "@zapier/design-system";
import { useSetAtom } from "jotai";
import { useCallback, useRef } from "react";
import {
  closeSecondaryToolbarMenuAtom,
  Service,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import styles from "./FloatingAppTypeahead.module.css";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { useAppSearchData } from "../../NodeSidebar/AppTypeahead/useAppSearchData";
import useServiceById from "../../../hooks/useServiceById";
import cleanUpAppId from "../../../utils/cleanUpAppId";
import { determineAssetType } from "../../../utils/determineAssetType";
import { AppIcon } from "../../AppIcon";
import { SelectedItem } from "../SelectedItem";
import { NodeToolbarTop } from "../NodeToolbarTop";
import { MenuWrapper } from "../MenuWrapper";

type Props = {
  selectedApps?: Service[];
};

export function FloatingAppTypeahead({ selectedApps }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const hasApp = selectedApps && selectedApps.length > 0;
  const getService = useServiceById();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuAtom);

  useFocusInput(inputRef);

  const { apps, loading, onSearch, setSearchQuery } = useAppSearchData({
    limit: 10,
    searchDebounceDelay: 300,
    excludeUpcoming: true,
    includeIsAppAdmin: true,
    includeIsAppCollaborator: true,
    apiHostname: "", // relative queries
  });

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      const selectedApp = apps.find((app) => app.id === key);

      if (key && selectedApp) {
        takeSnapshot();

        const assetType = determineAssetType(
          [
            {
              currentImplementation: {
                id: selectedApp.currentImplementation.id,
              },
            },
          ],
          "default",
        );

        updateNodeAndSave({
          type: "default",
          data: {
            // Read by the backend to send to Zap Guesser
            app: selectedApp.name,
            services: [
              {
                currentImplementation: {
                  id: selectedApp.currentImplementation.id,
                },
              },
            ],
            ...(assetType ? { color: undefined } : {}), // Clear color in order to fall back to default asset node colors
          },
        });

        setSearchQuery("");
        closeSecondaryMenu();
      }
    },
    [apps, takeSnapshot, updateNodeAndSave, setSearchQuery, closeSecondaryMenu],
  );

  const onRemoveApp = useCallback(() => {
    takeSnapshot();

    updateNodeAndSave({
      data: {
        app: undefined,
        services: undefined,
        tableId: undefined,
        projectId: undefined,
        pageId: undefined,
        chatbotId: undefined,
      },
    });
  }, [updateNodeAndSave, takeSnapshot]);

  return (
    <NodeToolbarTop>
      <MenuWrapper>
        {hasApp ? (
          <ul className={styles.selectedList}>
            {selectedApps.map((selectedApp) => {
              const appId = selectedApp.currentImplementation.id;
              const service = getService(appId);
              const serviceName = service?.name ?? cleanUpAppId(appId);

              return (
                <li
                  key={selectedApp.currentImplementation.id}
                  className={styles.selectedItem}
                >
                  <SelectedItem
                    label={serviceName}
                    icon={<AppIcon app={selectedApp} hasBorder={false} />}
                    clearSelection={onRemoveApp}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles.floatingTypeaheadWrapper}>
            <Typeahead
              aria-label="Search apps"
              placeholder="Search apps"
              ref={inputRef}
              onInputChange={onSearch}
              onSelectionChange={onSelectionChange}
              isBusy={loading}
              classNamePopOver={styles.popover}
              size="medium"
            >
              {apps.map((app) => {
                return (
                  <Typeahead.Item
                    key={app.id}
                    id={app.id}
                    textValue={app.name}
                    className={styles.floatingTypeaheadItem}
                    size="medium"
                  >
                    <AppIcon app={app} />
                    <div className={styles.truncateText}>
                      <Text type="SmallPrint2">{app.name}</Text>
                    </div>
                  </Typeahead.Item>
                );
              })}
            </Typeahead>
          </div>
        )}
      </MenuWrapper>
    </NodeToolbarTop>
  );
}
